import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, FolderKanban, FileText, CheckCircle2, Bell, MessageSquare,
  ArrowRight, Loader2, ChevronRight, Target, Calendar
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ONBOARDING_STEPS = [
  { id: 'welcome', title: 'Welcome', icon: Sparkles },
  { id: 'projects', title: 'Your Projects', icon: FolderKanban },
  { id: 'actions', title: 'Pending Actions', icon: CheckCircle2 },
  { id: 'done', title: 'Get Started', icon: ArrowRight },
];

export default function ClientOnboardingScreen({ onComplete }) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('d3_token');
    fetch(`${API_URL}/api/invites/onboarding/data`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
      .then(r => r.ok ? r.json() : { orgName: '', projects: [], pendingApprovals: [], recentUpdates: [], notifications: [] })
      .then(setData)
      .catch(() => setData({ orgName: '', projects: [], pendingApprovals: [], recentUpdates: [], notifications: [] }))
      .finally(() => setLoading(false));
  }, []);

  const handleComplete = async () => {
    const token = localStorage.getItem('d3_token');
    await fetch(`${API_URL}/api/invites/onboarding/complete`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    }).catch(() => {});
    onComplete();
  };

  const nextStep = () => {
    if (step < ONBOARDING_STEPS.length - 1) setStep(step + 1);
    else handleComplete();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8 px-4" data-testid="client-onboarding">
      {/* Progress */}
      <div className="flex items-center gap-2 mb-8">
        {ONBOARDING_STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2 flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
              i <= step ? 'bg-indigo-600 text-white' : 'bg-white/5 text-zinc-600'
            }`}>
              {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
            </div>
            {i < ONBOARDING_STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 rounded ${i < step ? 'bg-indigo-500' : 'bg-white/5'}`} />
            )}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>

          {/* Step 0: Welcome */}
          {step === 0 && (
            <div className="text-center" data-testid="onboarding-welcome">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-indigo-500 flex items-center justify-center mx-auto mb-6 shadow-[0_0_40px_rgba(79,70,229,0.3)]">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Welcome to {data?.orgName || 'D3 Command'}</h1>
              <p className="text-base text-zinc-400 mb-2">Hi {data?.userName || user?.name || 'there'}!</p>
              <p className="text-sm text-zinc-500 max-w-md mx-auto mb-8">
                Your client portal is ready. Here you can track project progress, review deliverables,
                approve milestones, and communicate directly with your team.
              </p>
              <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto mb-8">
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 text-center">
                  <FolderKanban className="w-6 h-6 text-indigo-400 mx-auto mb-2" />
                  <p className="text-[10px] text-zinc-500">Track Projects</p>
                </div>
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 text-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                  <p className="text-[10px] text-zinc-500">Approve Deliverables</p>
                </div>
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 text-center">
                  <MessageSquare className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                  <p className="text-[10px] text-zinc-500">Collaborate</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Projects */}
          {step === 1 && (
            <div data-testid="onboarding-projects">
              <h2 className="text-xl font-bold text-white mb-1">Your Active Projects</h2>
              <p className="text-sm text-zinc-500 mb-6">
                {data?.projects?.length > 0
                  ? `You have ${data.projects.length} active project${data.projects.length > 1 ? 's' : ''}`
                  : 'No projects assigned yet. You will be notified when a project is ready.'}
              </p>
              <div className="space-y-3">
                {(data?.projects || []).map(p => (
                  <div key={p.id} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-indigo-600/20 flex items-center justify-center">
                      <FolderKanban className="w-5 h-5 text-indigo-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-white">{p.name}</h3>
                      <p className="text-xs text-zinc-500 capitalize">{p.status?.replace('_', ' ')}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-white">{p.progressPercent || 0}%</p>
                      <p className="text-[10px] text-zinc-600">Progress</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Pending Actions */}
          {step === 2 && (
            <div data-testid="onboarding-actions">
              <h2 className="text-xl font-bold text-white mb-1">Pending Actions</h2>
              <p className="text-sm text-zinc-500 mb-6">Items that need your attention</p>

              {(data?.pendingApprovals || []).length > 0 ? (
                <div className="space-y-3 mb-6">
                  <p className="text-xs text-zinc-400 font-medium uppercase tracking-wider">Awaiting Your Approval</p>
                  {data.pendingApprovals.map(a => (
                    <div key={a.id} className="bg-amber-500/5 border border-amber-500/10 rounded-xl p-4 flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-amber-400 shrink-0" />
                      <div>
                        <p className="text-sm text-white">{a.name}</p>
                        <p className="text-[10px] text-zinc-500">Requires your review</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-8 text-center mb-6">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-sm text-zinc-400">No pending actions right now</p>
                </div>
              )}

              {(data?.recentUpdates || []).length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-zinc-400 font-medium uppercase tracking-wider">Recent Updates</p>
                  {data.recentUpdates.slice(0, 3).map(u => (
                    <div key={u.id} className="flex items-start gap-3 text-xs text-zinc-400 bg-white/[0.02] px-3 py-2 rounded-lg">
                      <Bell className="w-3.5 h-3.5 text-zinc-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-zinc-300">{u.title}</p>
                        <p className="text-zinc-600">{new Date(u.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Done */}
          {step === 3 && (
            <div className="text-center" data-testid="onboarding-done">
              <div className="w-16 h-16 rounded-full bg-emerald-600/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">You are All Set!</h2>
              <p className="text-sm text-zinc-400 max-w-sm mx-auto mb-8">
                Your portal is ready. You can access your projects, review deliverables, and communicate
                with your team at any time.
              </p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex justify-between mt-8">
        {step > 0 ? (
          <button onClick={() => setStep(step - 1)} className="px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="onboarding-back">
            Back
          </button>
        ) : <div />}
        <button onClick={nextStep}
          className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-xl transition-colors flex items-center gap-2"
          data-testid="onboarding-next"
        >
          {step === ONBOARDING_STEPS.length - 1 ? 'Go to Dashboard' : 'Continue'} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
